import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiServiceService } from '../api-service.service';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { SharedValidators } from '../shared/validators/shared-validators';

@Component({
  selector: 'app-ota-batch-reports',
  templateUrl: './ota-batch-reports.component.html',
  styleUrls: ['./ota-batch-reports.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    MatDatepickerInput,
    MatNativeDateModule,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    NgxPaginationModule,
    CommonPaginationComponent,
  ],
})
export class OtaBatchReportsComponent implements OnInit {
  size = 10;
  page = 1;
  listOfData = [];
  exportData = [];

  today = new Date();
  fromDate = '';
  toDate = '';
  fromBatchId = '';
  toBatchId = '';

  otaReportForm: FormGroup = this.formBuilder.group({});
  isDownloading = false;

  userPermission = [];
  otaBatchReportsView = false;

  // storage listener for cross-tab sync
  private storageListener = (event: StorageEvent) => {
    if (event.key === 'downloadInProgress') {
      this.isDownloading = event.newValue === 'true';
    }
  };

  constructor(
    public apiMicroservice: ApiServiceService,
    private formBuilder: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.checkPermission();
    if (this.otaBatchReportsView) {
      this.setOtaBatchReportForm();
      this.syncInitialDownloadState();
    }
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (element.pageName === 'OTA Batch' && element.view === false) {
          this.navigateBack();
        }
        if (element.pageName === 'OTA Batch' && element.view === true) {
          this.otaBatchReportsView = true;
        }
      }
    );
  }

  setOtaBatchReportForm() {
    this.otaReportForm = this.formBuilder.group(
      {
        fromDate: [''],
        toDate: [''],
        fromBatchId: [''],
        toBatchId: [''],
      },
      { validators: SharedValidators.dateRangeValidator('fromDate', 'toDate') }
    );
  }
  syncInitialDownloadState() {
    // sync initial download state across tabs
    this.isDownloading = localStorage.getItem('downloadInProgress') === 'true';
    window.addEventListener('storage', this.storageListener);
  }

  clear() {
    this.otaReportForm.controls['fromDate'].setValue('');
    this.otaReportForm.controls['toDate'].setValue('');
    this.otaReportForm.controls['fromBatchId'].setValue('');
    this.otaReportForm.controls['toBatchId'].setValue('');

    this.otaReportForm.controls['fromDate'].clearValidators();
    this.otaReportForm.controls['toDate'].clearValidators();
    this.otaReportForm.controls['fromDate'].updateValueAndValidity();
    this.otaReportForm.controls['toDate'].updateValueAndValidity();

    this.otaReportForm.controls['fromBatchId'].clearValidators();
    this.otaReportForm.controls['toBatchId'].clearValidators();
    this.otaReportForm.controls['fromBatchId'].updateValueAndValidity();
    this.otaReportForm.controls['toBatchId'].updateValueAndValidity();

    this.otaReportForm.reset();
    this.listOfData = [];
  }

  getReports() {
    const reportsData = this.otaReportForm.value;
    // Validate fromDate and toDate
    if (reportsData.fromDate) {
      reportsData.fromDate = reportsData.fromDate.toISOString();
      this.otaReportForm.controls['toDate'].setValidators([
        Validators.required,
      ]);
      this.otaReportForm.controls['toDate'].updateValueAndValidity();
      this.otaReportForm.get('toDate')?.markAsTouched();
    } else {
      this.otaReportForm.controls['toDate'].clearValidators();
      this.otaReportForm.controls['toDate'].updateValueAndValidity();
      reportsData.fromDate = '';
    }

    if (reportsData.toDate) {
      this.otaReportForm.controls['fromDate'].setValidators([
        Validators.required,
      ]);
      this.otaReportForm.controls['fromDate'].updateValueAndValidity();
      this.otaReportForm.get('fromDate')?.markAsTouched();
      reportsData.toDate = reportsData.toDate.toISOString();
      // Check if fromDate is greater than toDate
      if (new Date(reportsData.fromDate) > new Date(reportsData.toDate)) {
        this.otaReportForm.controls['fromDate'].setErrors({
          invalidDateRange: true,
        });
        return;
      }
    } else {
      this.otaReportForm.controls['fromDate'].clearValidators();
      this.otaReportForm.controls['fromDate'].updateValueAndValidity();
      reportsData.toDate = '';
    }

    // Validate fromBatchId and toBatchId
    if (reportsData.fromBatchId) {
      this.otaReportForm.controls['toBatchId'].setValidators(
        Validators.required
      );
      this.otaReportForm.controls['toBatchId'].updateValueAndValidity();
      this.otaReportForm.get('toBatchId')?.markAsTouched();
    } else {
      this.otaReportForm.controls['toBatchId'].clearValidators();
      this.otaReportForm.controls['toBatchId'].updateValueAndValidity();
    }

    if (reportsData.toBatchId) {
      this.otaReportForm.controls['fromBatchId'].setValidators(
        Validators.required
      );
      this.otaReportForm.controls['fromBatchId'].updateValueAndValidity();
      this.otaReportForm.get('fromBatchId')?.markAsTouched();

      // Check if fromBatchId is greater than toBatchId
      if (parseInt(reportsData.fromBatchId) > parseInt(reportsData.toBatchId)) {
        this.otaReportForm.controls['fromBatchId'].setErrors({
          invalidBatchIdRange: true,
        });
        return;
      }
    } else {
      this.otaReportForm.controls['fromBatchId'].clearValidators();
      this.otaReportForm.controls['fromBatchId'].updateValueAndValidity();
    }

    // Reset errors if none of the required fields are filled
    if (!reportsData.fromDate && !reportsData.toDate) {
      this.otaReportForm.controls['fromDate'].clearValidators();
      this.otaReportForm.controls['toDate'].clearValidators();
      this.otaReportForm.controls['fromDate'].updateValueAndValidity();
      this.otaReportForm.controls['toDate'].updateValueAndValidity();
    }

    if (!reportsData.fromBatchId && !reportsData.toBatchId) {
      this.otaReportForm.controls['fromBatchId'].clearValidators();
      this.otaReportForm.controls['toBatchId'].clearValidators();
      this.otaReportForm.controls['fromBatchId'].updateValueAndValidity();
      this.otaReportForm.controls['toBatchId'].updateValueAndValidity();
    }
    // Validate if nothing is entered
    if (
      !reportsData.fromDate &&
      !reportsData.toDate &&
      !reportsData.fromBatchId &&
      !reportsData.toBatchId
    ) {
      this.otaReportForm.controls['fromDate'].setValidators(
        Validators.required
      );
      this.otaReportForm.controls['fromDate'].updateValueAndValidity();
      this.otaReportForm.get('fromDate')?.markAsTouched();
      this.otaReportForm.controls['fromBatchId'].setValidators(
        Validators.required
      );
      this.otaReportForm.controls['fromBatchId'].updateValueAndValidity();
      this.otaReportForm.get('fromBatchId')?.markAsTouched();
    }

    if (this.otaReportForm.invalid) {
      this.otaReportForm.get('fromDate')?.markAsTouched();
      this.otaReportForm.get('toDate')?.markAsTouched();
      this.otaReportForm.get('fromBatchId')?.markAsTouched();
      this.otaReportForm.get('toBatchId')?.markAsTouched();
    } else {
      // Adjust fromDate to the start of the day, only if it's provided
      const fromDate = reportsData.fromDate
        ? new Date(reportsData.fromDate)
        : null;
      if (fromDate) {
        fromDate.setHours(0, 0, 0, 0);
      }

      // Adjust toDate to the end of the day, only if it's provided
      const toDate = reportsData.toDate ? new Date(reportsData.toDate) : null;
      if (toDate) {
        toDate.setHours(23, 59, 59, 999);
      }

      // Convert the dates to ISO strings only if they exist
      const finalFromDate = fromDate ? fromDate.toISOString() : '';
      const finalToDate = toDate ? toDate.toISOString() : '';
      this.fromDate = finalFromDate;
      this.toDate = finalToDate;
      this.fromBatchId = reportsData.fromBatchId;
      this.toBatchId = reportsData.toBatchId;
      // Call the function with adjusted dates, passing only valid values
      this.getReportsCounts(
        this.fromDate,
        this.toDate,
        this.fromBatchId,
        this.toBatchId
      );
    }
  }

  getReportsCounts(
    fromDate: string,
    toDate: string,
    fromId: string,
    toId: string
  ) {
    this.page = 1;
    this.listOfData = [];
    this.spinner.show();
    this.apiMicroservice
      .getOtaBatchReports(fromDate, toDate, fromId, toId)
      .subscribe({
        next: (res) => {
          if (res.status) {
            this.listOfData = res.data;
            this.spinner.hide();
            this.apiMicroservice.openSnackbar('Fetched Successfully');
          } else {
            this.spinner.hide();
            console.error('Error: Status not true', res);
            this.apiMicroservice.openSnackbar(`Error: Status not true ${res}`);
          }
        },
        error: (error) => {
          // Handle network or server errors
          this.spinner.hide();
          console.error('An error occurred:', error);
          // Optionally, display a user-friendly message to the user
          this.apiMicroservice.openSnackbar(error.error.message);
          alert(
            'An error occurred while fetching the data. Please try again later.'
          );
        },
      });
  }

  onPageChange(event: number) {
    this.page = event;
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
  }

  downloadAllOTAPendingDevicesReport() {
    if (
      confirm(
        'This will download all the devices in the system which are under OTA process. Are you sure?'
      )
    ) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice.downloadAllOTAPendingDevicesReport().subscribe({
        next: (res) => {
          try {
            if (res?.status && res.fileDownloadUrl) {
              // window.open(res.fileDownloadUrl, '_blank');
              const link = document.createElement('a');
              link.href = res.fileDownloadUrl;
              link.download = ''; // let browser infer filename
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              this.apiMicroservice.openSnackbar('File ready for download');
            } else {
              this.apiMicroservice.openSnackbar(
                res?.message || 'File URL not available'
              );
            }
          } finally {
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          const example =
            ERROR.error.message ||
            'Download failed. Please contact server team.';
          this.apiMicroservice.openSnackbar(example);
          // clear in-progress state
          this.isDownloading = false;
          localStorage.setItem('downloadInProgress', 'false');
        },
      });
    }
  }

  downloadOtaOverallBatchDeviceReport() {
    if (
      confirm(
        'This will download all the devices in the batch summary report. Are you sure?'
      )
    ) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice
        .downloadOtaOverallBatchDeviceReport(
          this.fromDate,
          this.toDate,
          this.fromBatchId,
          this.toBatchId
        )
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                // window.open(res.fileDownloadUrl, '_blank');
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  downloadOtaBatchSummaryReport() {
    if (
      confirm('This will download the OTA batch Summary report. Are you sure?')
    ) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice
        .downloadOtaBatchSummaryReport(
          this.fromDate,
          this.toDate,
          this.fromBatchId,
          this.toBatchId
        )
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                // window.open(res.fileDownloadUrl, '_blank');
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
